/**
 * Story `be-fe-contactgegevens-bevestigen` — de GEDEELDE logica van "contactgegevens bevestigen" voor kantoor én
 * monteur-app (P-10): de poort op whoami, de teksten, de invoerregels en de vertaling van de API-antwoorden naar een
 * uitkomst. Geen React en geen scherm; dit bestand staat onder `src/auth/` en zit dus al in de keten van `/monteur`.
 *
 * Concept `contactgegevens-bevestigen-v1.html` v1.1: schermen A (invullen), B (e-mailcode), E (foute code) en D1
 * (klaar), plus de monteur-variant. Scherm C en D2 (sms) zijn vervallen (C-4).
 *
 * ⚠ De backend is de autoriteit: hij valideert adres en nummer (400 `email_invalid` / `mobile_invalid`) en weigert
 * elke andere call met 403 `contact_verification_required`. De regels hier zijn alleen comfort (de knop dicht en een
 * melding bij het veld vóór het versturen).
 */
import { ApiError, type WhoAmIResponse } from '@carculate/api-client';

import { apiClient } from '@/auth/apiClient';
import { isNetworkError } from '@/lib/fitter-offline/rules';

// ─── De poort (AC 7, 17) ───────────────────────────────────────────────────────────────────────────

/**
 * Staat de stap open voor deze sessie? Alleen `=== true` telt: een ontbrekend veld (een gebruiker met een eigen
 * adres, of een oudere backend) is "geen stap" — dezelfde fail-open-richting als de backend (P-4).
 */
export function isContactVerificationRequired(user: WhoAmIResponse | null | undefined): boolean {
    return user?.contactVerificationRequired === true;
}

/** Window-event bij een 403 `contact_verification_required` van een willekeurige call (centraal, niet per call). */
export { CONTACT_VERIFICATION_REQUIRED_EVENT } from './contactVerificationEvent';

/**
 * Het whoami-antwoord na een 403 `contact_verification_required`: hetzelfde antwoord met het veld aan. Geen extra
 * call — de backend zegt het net zelf. Zonder gebruiker (nog niet geladen) blijft het zoals het is.
 */
export function withContactVerificationRequired(user: WhoAmIResponse | null | undefined): WhoAmIResponse | null | undefined {
    if (!user || user.contactVerificationRequired === true) return user;
    return { ...user, contactVerificationRequired: true };
}

/** De ENE query-key van `GET /api/auth/contact-verification`. Bevat geen geheim. */
export const QK_CONTACT_VERIFICATION = ['auth', 'contact-verification'] as const;

// ─── Teksten (concept v1.1) ────────────────────────────────────────────────────────────────────────

export const CONTACT_VERIFICATION_TEXTS = {
    // A (kantoor)
    formTitle: 'Vul je contactgegevens aan',
    formIntro: 'We hebben een eigen e-mailadres en mobiel nummer van je nodig, onder andere om je wachtwoord te kunnen herstellen.',
    nameLabel: 'Naam',
    emailLabel: 'E-mailadres',
    mobileLabel: 'Mobiel nummer (niet verplicht)',
    mobileHint: 'Voorgevuld met het nummer dat we al hadden. Klopt het? Dan hoef je niets te doen.',
    sendCode: 'Stuur bevestigingscode',
    sending: 'Versturen…',
    // B (kantoor)
    codeTitle: 'Bevestig je e-mailadres',
    codeLabel: 'Code',
    resend: 'Stuur opnieuw',
    otherAddress: 'Ander adres invullen',
    confirm: 'Bevestigen',
    confirming: 'Bevestigen…',
    alreadySent: 'De code is net al verstuurd. Kijk in je mail, ook in de map met ongewenste mail.',
    resent: 'Er is een nieuwe code verstuurd.',
    // E
    codeExpired: 'Deze code is niet meer geldig. Vraag een nieuwe code aan.',
    // D1
    doneTitle: 'Gelukt',
    doneEmail: 'E-mailadres bevestigd',
    doneMobile: 'Mobiel nummer opgeslagen',
    doneHint: 'Je kunt nu zelf je wachtwoord herstellen via Wachtwoord vergeten? op het inlogscherm.',
    continue: 'Verder naar CARculate',
    // Monteur-variant
    fitterFormTitle: 'Je gegevens',
    fitterFormIntro: 'Vul je eigen e-mailadres en mobiele nummer in. Die hebben we nodig om je wachtwoord te kunnen herstellen.',
    fitterSendCode: 'Stuur code',
    fitterCodeTitle: 'Code uit je mail',
    fitterOffline: 'Geen bereik? Deze stap werkt alleen online; je kunt hem afmaken zodra je weer verbinding hebt.',
    // Veldmeldingen en fouten
    emailInvalid: 'Vul een geldig e-mailadres in.',
    mobileInvalid: 'Vul een Nederlands mobiel nummer in, bijvoorbeeld 06 12345678 of +31 6 12345678.',
    codeFormat: 'De code bestaat uit 6 cijfers.',
    failed: 'Dat is niet gelukt. Probeer het opnieuw.',
    loadFailed: 'Je gegevens konden niet worden geladen.',
    retry: 'Opnieuw proberen',
    loading: 'Laden…',
    logout: 'Uitloggen',
} as const;

const T = CONTACT_VERIFICATION_TEXTS;

/** Scherm B: "We hebben een code gestuurd naar …. Hij is 15 minuten geldig." */
export function codeSentText(sentTo: string): string {
    return `We hebben een code gestuurd naar ${sentTo}. Hij is 15 minuten geldig.`;
}

/** Scherm E: "Deze code klopt niet. Je kunt het nog N keer proberen." */
export function codeWrongText(attemptsLeft: number): string {
    return `Deze code klopt niet. Je kunt het nog ${attemptsLeft} keer proberen.`;
}

/** 409 `resend_too_soon` / `too_many_codes`: wanneer het weer kan. */
export function retryAfterText(seconds: number): string {
    if (seconds < 60) return `Je kunt over ${seconds} seconden een nieuwe code aanvragen.`;
    const minutes = Math.ceil(seconds / 60);
    return minutes === 1
        ? 'Je hebt te vaak een code aangevraagd. Probeer het over 1 minuut opnieuw.'
        : `Je hebt te vaak een code aangevraagd. Probeer het over ${minutes} minuten opnieuw.`;
}

/** "Stuur opnieuw (nog 42 s)". */
export function resendCountdownText(seconds: number): string {
    return `${T.resend} (nog ${seconds} s)`;
}

// ─── Invoerregels (AC 8, 10) ───────────────────────────────────────────────────────────────────────

/** Bovengrenzen van de velden (de backend toetst de echte kolomlengte). */
export const EMAIL_MAX = 200; // RB_Contact.EmailAddress is nvarchar(200); de server weigert langer met email_invalid
export const MOBILE_MAX = 30;

/**
 * Een lichte vormtoets van het e-mailadres (getrimd): precies één `@`, iets ervoor, een domein met een punt, geen
 * spatie, komma of puntkomma. De backend (`SupportInput.IsValidEmail`) beslist.
 */
export function isPlausibleEmail(raw: string): boolean {
    const v = raw.trim();
    if (v === '' || v.length > EMAIL_MAX) return false;
    if (/[\s;,]/.test(v)) return false;
    const at = v.indexOf('@');
    if (at <= 0 || v.indexOf('@', at + 1) >= 0) return false;
    const domain = v.slice(at + 1);
    if (domain === '' || domain.startsWith('.') || domain.endsWith('.')) return false;
    return domain.includes('.');
}

/**
 * Het mobiele nummer (AC 8): leeg mag; anders `06` + 8 cijfers of `+31 6` + 8 cijfers, met spaties en streepjes.
 * Opgeslagen wordt het zoals ingevuld (alleen getrimd).
 */
export function isValidMobile(raw: string): boolean {
    const v = raw.trim();
    if (v === '') return true;
    if (v.length > MOBILE_MAX) return false;
    if (!/^[+\d\s-]+$/.test(v)) return false;
    const compact = v.replace(/[\s-]/g, '');
    return /^06\d{8}$/.test(compact) || /^\+316\d{8}$/.test(compact);
}

/** Alleen cijfers, hooguit 6 (het invoerveld van de code). */
export function sanitizeCode(raw: string): string {
    return raw.replace(/\D/g, '').slice(0, 6);
}

/** Strikt exact 6 cijfers (AC 10); anders versturen we niet. */
export function isCompleteCode(code: string): boolean {
    return /^\d{6}$/.test(code);
}

// ─── "Stuur opnieuw" (AC 9/10) ─────────────────────────────────────────────────────────────────────

/** De standaardwachttijd tot "Stuur opnieuw" (60 s). */
export const RESEND_WAIT_MS = 60_000;

/**
 * Vanaf welk tijdstip (klok van dit toestel) "Stuur opnieuw" kan. Een tijd van de server wordt nooit later dan
 * nu + 60 s genomen (een verzette klok mag de knop niet urenlang dichthouden); zonder bruikbare tijd: nu + 60 s.
 */
export function resendAtFrom(resendAvailableAt: string | null | undefined, now: number): number {
    const fallback = now + RESEND_WAIT_MS;
    const parsed = resendAvailableAt ? Date.parse(resendAvailableAt) : Number.NaN;
    if (Number.isNaN(parsed)) return fallback;
    return Math.min(Math.max(parsed, now), fallback);
}

/** Hele seconden tot `resendAt` (0 = kan nu). */
export function secondsUntil(resendAt: number, now: number): number {
    return Math.max(0, Math.ceil((resendAt - now) / 1000));
}

// ─── Uitkomsten van de calls ───────────────────────────────────────────────────────────────────────

function bodyOf(err: unknown): Record<string, unknown> | null {
    if (!(err instanceof ApiError)) return null;
    const b = err.body;
    return b !== null && typeof b === 'object' ? (b as Record<string, unknown>) : null;
}

function reasonOf(err: unknown): string | null {
    const r = bodyOf(err)?.reason;
    return typeof r === 'string' ? r : null;
}

function numberOf(err: unknown, key: string): number | null {
    const v = bodyOf(err)?.[key];
    return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null;
}

/** Geen verbinding (monteur-tekst) of iets anders. */
export function isOfflineFailure(err: unknown): boolean {
    if (isNetworkError(err)) return true;
    return typeof navigator !== 'undefined' && navigator.onLine === false && !(err instanceof ApiError);
}

export type RequestCodeOutcome =
    | { kind: 'sent'; sentTo: string; expiresAt: string; resendAvailableAt: string | null; alreadySent: boolean }
    | { kind: 'emailInvalid' }
    | { kind: 'mobileInvalid' }
    | { kind: 'tooSoon'; retryAfterSeconds: number }
    | { kind: 'tooMany'; retryAfterSeconds: number }
    | { kind: 'notRequired' }
    | { kind: 'offline'; error: unknown }
    | { kind: 'error'; error: unknown };

/**
 * 409 `contact_verification_not_required`: de stap is intussen niet meer nodig (een beheerder vulde een eigen adres
 * in, of de stap is in een ander tabblad / op de telefoon afgerond). Geen fout: whoami verversen, dan valt de poort weg.
 */
const NOT_REQUIRED_REASON = 'contact_verification_not_required';

/** `POST request-code` (AC 8/9). Het adres wordt getrimd verstuurd; het mobiele nummer getrimd. */
export async function requestCode(email: string, mobile: string): Promise<RequestCodeOutcome> {
    const trimmedEmail = email.trim();
    try {
        const res = await apiClient.requestContactVerificationCode({ email: trimmedEmail, mobile: mobile.trim() });
        return {
            kind: 'sent',
            sentTo: typeof res?.sentTo === 'string' && res.sentTo !== '' ? res.sentTo : trimmedEmail,
            expiresAt: typeof res?.expiresAt === 'string' ? res.expiresAt : '',
            resendAvailableAt: typeof res?.resendAvailableAt === 'string' ? res.resendAvailableAt : null,
            alreadySent: res?.alreadySent === true,
        };
    } catch (err) {
        const reason = reasonOf(err);
        if (reason === 'email_invalid') return { kind: 'emailInvalid' };
        if (reason === 'mobile_invalid') return { kind: 'mobileInvalid' };
        if (reason === 'resend_too_soon') return { kind: 'tooSoon', retryAfterSeconds: numberOf(err, 'retryAfterSeconds') ?? 60 };
        if (reason === 'too_many_codes') return { kind: 'tooMany', retryAfterSeconds: numberOf(err, 'retryAfterSeconds') ?? 3600 };
        if (reason === NOT_REQUIRED_REASON) return { kind: 'notRequired' };
        return { kind: isOfflineFailure(err) ? 'offline' : 'error', error: err };
    }
}

export type ConfirmOutcome =
    | { kind: 'confirmed'; mobileSaved: boolean | null }
    | { kind: 'wrong'; attemptsLeft: number }
    | { kind: 'expired' }
    | { kind: 'invalidFormat' }
    | { kind: 'notRequired' }
    | { kind: 'offline'; error: unknown }
    | { kind: 'error'; error: unknown };

/** `POST confirm` (AC 10-12): alleen de code. */
export async function confirmCode(code: string): Promise<ConfirmOutcome> {
    try {
        const res = await apiClient.confirmContactVerification({ code });
        return { kind: 'confirmed', mobileSaved: typeof res?.mobileSaved === 'boolean' ? res.mobileSaved : null };
    } catch (err) {
        const reason = reasonOf(err);
        if (reason === 'code_wrong') return { kind: 'wrong', attemptsLeft: numberOf(err, 'attemptsLeft') ?? 0 };
        if (reason === 'code_expired' || (err instanceof ApiError && err.status === 410)) return { kind: 'expired' };
        if (reason === 'code_invalid') return { kind: 'invalidFormat' };
        if (reason === NOT_REQUIRED_REASON) return { kind: 'notRequired' };
        return { kind: isOfflineFailure(err) ? 'offline' : 'error', error: err };
    }
}
